import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ClipboardList, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DECISIONS = ["Fichar", "Seguir", "Ver en directo", "Descartar", "Pendiente"];
const blank = () => ({
  observation_date: new Date().toISOString().slice(0, 10), match_context: "", observed_position: "",
  technical_notes: "", tactical_notes: "", physical_notes: "", speed_notes: "",
  decision_notes: "", behavior_notes: "", strengths: "", weaknesses: "",
  description: "", rating: "", decision: "Pendiente", observations: "",
});

const DECISION_BADGE = {
  Fichar: "bg-emerald-100 text-emerald-700",
  Seguir: "bg-purple-100 text-purple-800",
  "Ver en directo": "bg-sky-100 text-sky-800",
  Descartar: "bg-rose-100 text-rose-700",
  Pendiente: "bg-slate-100 text-slate-600",
};

export default function RivalScoutingReports({ jugadorId }) {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const canEdit = user?.role !== "viewer";

  const load = () => api.get(`/rival-dataset/players/${jugadorId}/reports`).then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jugadorId]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rival-dataset/players/${jugadorId}/reports`, {
        ...form, rating: form.rating ? parseInt(form.rating, 10) : null,
      });
      toast.success("Informe de scouting guardado");
      setOpen(false);
      setForm(blank());
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm("¿Eliminar este informe?")) return;
    try {
      await api.delete(`/rival-dataset/reports/${r.id}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const F = ({ label, k, rows = 2 }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Textarea rows={rows} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`report-${k}`} />
    </div>
  );

  return (
    <Card className="border-slate-200" data-testid="rival-scouting-reports">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" style={{ color: "#D9B300" }} /> Informes de scouting ({reports.length})
          </div>
          {canEdit && (
            <Button size="sm" onClick={() => setOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-bold h-8" data-testid="rival-report-new">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Crear informe
            </Button>
          )}
        </div>
        {reports.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Sin informes todavía.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {reports.map((r) => (
              <li key={r.id} className="py-3 space-y-1" data-testid={`rival-report-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-bold">
                    {r.observation_date || "Sin fecha"} · {r.created_by_name}
                    {r.observed_position && <span className="text-slate-500 font-normal"> · {r.observed_position}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.rating != null && <span className="text-sm font-black" style={{ color: "#D9B300" }}>{r.rating}/10</span>}
                    {r.decision && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DECISION_BADGE[r.decision] || "bg-slate-100"}`}>{r.decision}</span>}
                    {(user?.role === "admin" || r.created_by === user?.id) && (
                      <button onClick={() => remove(r)} data-testid={`rival-report-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-rose-600" />
                      </button>
                    )}
                  </div>
                </div>
                {r.match_context && <div className="text-xs text-slate-500">Partido: {r.match_context}</div>}
                <div className="text-xs text-slate-600 space-y-0.5">
                  {r.technical_notes && <div><b>Técnica:</b> {r.technical_notes}</div>}
                  {r.tactical_notes && <div><b>Táctica:</b> {r.tactical_notes}</div>}
                  {r.physical_notes && <div><b>Físico:</b> {r.physical_notes}</div>}
                  {r.speed_notes && <div><b>Velocidad:</b> {r.speed_notes}</div>}
                  {r.decision_notes && <div><b>Toma de decisión:</b> {r.decision_notes}</div>}
                  {r.behavior_notes && <div><b>Comportamiento:</b> {r.behavior_notes}</div>}
                  {r.strengths && <div className="text-emerald-700"><b>Fortalezas:</b> {r.strengths}</div>}
                  {r.weaknesses && <div className="text-rose-700"><b>Debilidades:</b> {r.weaknesses}</div>}
                  {r.description && <div>{r.description}</div>}
                  {r.observations && <div className="italic">{r.observations}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader><DialogTitle>Informe de scouting</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fecha</Label>
                  <Input type="date" value={form.observation_date} onChange={(e) => setForm((f) => ({ ...f, observation_date: e.target.value }))} data-testid="report-date" />
                </div>
                <div>
                  <Label className="text-xs">Posición observada</Label>
                  <Input value={form.observed_position} onChange={(e) => setForm((f) => ({ ...f, observed_position: e.target.value }))} data-testid="report-position" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Partido / contexto</Label>
                <Input value={form.match_context} onChange={(e) => setForm((f) => ({ ...f, match_context: e.target.value }))} placeholder="Ej. vs EF Concepción Aficionado A, J12" data-testid="report-match" />
              </div>
              <F label="Técnica" k="technical_notes" />
              <F label="Táctica" k="tactical_notes" />
              <F label="Físico" k="physical_notes" />
              <div className="grid grid-cols-2 gap-3">
                <F label="Velocidad" k="speed_notes" />
                <F label="Toma de decisión" k="decision_notes" />
              </div>
              <F label="Comportamiento" k="behavior_notes" />
              <div className="grid grid-cols-2 gap-3">
                <F label="Fortalezas" k="strengths" />
                <F label="Debilidades" k="weaknesses" />
              </div>
              <F label="Descripción general" k="description" rows={3} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Valoración (1-10)</Label>
                  <Input type="number" min="1" max="10" value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))} data-testid="report-rating" />
                </div>
                <div>
                  <Label className="text-xs">Decisión</Label>
                  <Select value={form.decision} onValueChange={(v) => setForm((f) => ({ ...f, decision: v }))}>
                    <SelectTrigger data-testid="report-decision"><SelectValue /></SelectTrigger>
                    <SelectContent>{DECISIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <F label="Observaciones" k="observations" />
              <DialogFooter>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="report-save">
                  Guardar informe
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
